/**
 * Minecart Switchyard (Fun section). Switches are plain buttons on the yard (tap, click, or Tab +
 * Enter); everything else is drawn. Every switch is set before Run. The browser plays the
 * engine's frames back on simulation time (pause, step, slow), so playback speed, frame rate or a
 * background tab never change what happens; the server re-runs the same engine for medals.
 */
import { LitElement, css, html, nothing, svg } from 'lit';
import {
  CARGO_NAME,
  DX,
  DY,
  MINECART_LEVELS,
  cartLabel,
  cartMedal,
  cellSize,
  failSpot,
  failText,
  nextLevelId,
  rewardById,
  runCarts,
  switchIds,
  type Cargo,
  type CartFrame,
  type CartRun,
  type Dir,
  type FailSpot,
  type Medal,
  type MinecartLevel,
  type SwitchBoard,
  type Track,
} from '@ka/fun';
import { markWalkthroughSeen, shared, walkthroughSeen, type KaWalkthrough } from '@ka/ui';
import { currentFun, invitation, itemIcon, loadFun, setFun, type FunView } from './fun.ts';
import { currentKid, kidApi } from './kid.ts';
import { WALK_LEVEL, minecartSteps, walkSwitchOk } from './minecart-walkthrough.ts';
import { navigate } from './router.ts';

/** Cargo carries a letter and a shape as well as a colour, so it never depends on colour alone. */
const CARGO: Record<Cargo, { letter: string; shape: string; color: string }> = {
  redstone: { letter: 'R', shape: '▲', color: 'var(--ka-red-bright)' },
  lapis: { letter: 'L', shape: '◆', color: '#4f7fe0' },
  emerald: { letter: 'E', shape: '●', color: 'var(--ka-foliage)' },
};
const SIDE = ['north', 'east', 'south', 'west'];
const TICK_MS = 600;
const SLOW = 0.3;
const GROUP = ['One cart', 'Two carts', 'Three carts'];
const plural = (n: number, one: string, many = `${one}es`) => `${n} ${n === 1 ? one : many}`;

const draftKey = (l: MinecartLevel) => `ka.fun.minecarts.${l.id}.v${l.version}`;
function loadDraft(l: MinecartLevel): SwitchBoard {
  try {
    const b = JSON.parse(localStorage.getItem(draftKey(l)) ?? 'null') as SwitchBoard | null;
    const ids = new Set(switchIds(l));
    if (b && b.switches && typeof b.switches === 'object')
      return {
        switches: Object.fromEntries(
          Object.entries(b.switches).filter(([id, v]) => ids.has(id) && typeof v === 'boolean'),
        ),
      };
  } catch {
    /* no draft */
  }
  return { switches: {} };
}
function saveDraft(l: MinecartLevel, b: SwitchBoard) {
  try {
    localStorage.setItem(draftKey(l), JSON.stringify(b));
  } catch {
    /* private mode: the yard still works, it just is not kept */
  }
}
const changed = (b: SwitchBoard) => Object.values(b.switches).filter(Boolean).length;

export class KaMinecarts extends LitElement {
  static override properties = {
    levelId: {},
    board: { state: true },
    result: { state: true },
    time: { state: true },
    playing: { state: true },
    slow: { state: true },
    hints: { state: true },
    server: { state: true },
    view: { state: true },
    cell: { state: true },
    sheetOpen: { state: true },
    walk: { state: true },
    walkStep: { state: true },
  };
  declare levelId: string;
  declare board: SwitchBoard;
  declare result: CartRun | null;
  /** Simulation time in ticks (fractional while a cart is between two rails). */
  declare time: number;
  declare playing: boolean;
  declare slow: boolean;
  declare hints: number;
  declare server: {
    state: 'idle' | 'saving' | 'saved' | 'error';
    medal?: Medal | null;
    newRewards?: string[];
    message?: string;
  };
  declare view: FunView | null;
  /** Yard square size in px (44..64), measured from the yard's box. */
  declare cell: number;
  declare private sheetOpen: boolean;
  /** The walkthrough is running on level 1, on a yard that is not saved as the kid's draft. */
  declare private walk: boolean;
  declare private walkStep: string | null;
  private walkNext = false;
  private walkChecked = false;
  private steps = minecartSteps({
    yard: () => this.renderRoot.querySelector('.yard'),
    sw: () => this.renderRoot.querySelector('.cell button'),
    run: () => this.renderRoot.querySelector('.run'),
    result: () => this.renderRoot.querySelector('.sheet') ?? this.renderRoot.querySelector('.yard'),
  });
  private raf = 0;
  private last = 0;

  static override styles = [
    shared,
    css`
      :host {
        display: grid;
        gap: 1rem;
      }
      :host > * {
        min-width: 0;
      }
      .row {
        display: flex;
        flex-wrap: wrap;
        gap: 0.5rem;
        align-items: center;
      }
      .row[hidden] {
        display: none;
      }
      .top {
        display: flex;
        flex-wrap: wrap;
        gap: 0.25rem 1rem;
        align-items: center;
        margin: 0;
      }
      .top a {
        min-height: 44px;
        display: inline-flex;
        align-items: center;
      }
      .title {
        font: 600 1.25rem var(--ka-font-display);
      }
      .play,
      .side,
      .board-col {
        display: grid;
        gap: 1rem;
        align-content: start;
        min-width: 0;
      }
      .goal {
        box-sizing: border-box;
        display: grid;
        gap: 0.5rem;
      }
      .goal p {
        margin: 0;
      }
      .carts-list {
        list-style: none;
        margin: 0;
        padding: 0;
        display: flex;
        flex-wrap: wrap;
        gap: 0.25rem 1rem;
      }
      .tag {
        font-weight: 700;
        padding: 0 0.3em;
        margin-right: 0.3em;
        border: 2px solid currentColor;
      }
      .yard-box {
        overflow-x: auto;
        min-width: 0;
      }
      .yard {
        position: relative;
        display: grid;
        width: fit-content;
        background: var(--ka-stone-900);
        border: 3px solid var(--ka-edge);
        touch-action: manipulation;
      }
      .cell {
        width: var(--cell);
        aspect-ratio: 1;
        display: grid;
        position: relative;
      }
      .cell svg,
      .cell button {
        width: 100%;
        height: 100%;
      }
      .cell button {
        all: unset;
        box-sizing: border-box;
        cursor: pointer;
        display: grid;
        background: var(--ka-stone-700);
      }
      .cell button:disabled {
        cursor: default;
      }
      .cell button:focus-visible {
        outline: 3px solid var(--ka-focus);
        outline-offset: -3px;
      }
      .cell button.changed {
        box-shadow: inset 0 0 0 3px var(--ka-gold);
      }
      .cell.marked {
        outline: 3px solid var(--ka-focus);
        outline-offset: -3px;
      }
      .cell.marked:focus {
        outline-width: 5px;
      }
      .callout {
        position: absolute;
        top: calc(100% + 4px);
        left: 0;
        padding: 0.2em 0.5em;
        background: var(--ka-focus);
        color: var(--ka-ink-on-gold);
        font: 600 0.8rem var(--ka-font-display);
        width: max-content;
        max-width: var(--room);
        box-sizing: border-box;
        pointer-events: none;
        /* Over the carts and the next squares; the marked square itself stays under the carts. */
        z-index: 3;
      }
      .callout.up {
        top: auto;
        bottom: calc(100% + 4px);
      }
      .callout.end {
        left: auto;
        right: 0;
      }
      .carts {
        position: absolute;
        inset: 0;
        width: 100%;
        height: 100%;
        pointer-events: none;
      }
      .board-col > p {
        margin: 0;
      }
      .dock,
      .sheet {
        display: grid;
        gap: 0.5rem;
        box-sizing: border-box;
      }
      .sheet h2,
      .sheet p,
      .help {
        margin: 0;
      }
      .bar {
        display: flex;
        gap: 0.5rem;
        align-items: center;
      }
      .bar .btn {
        min-width: 44px;
        justify-content: center;
        white-space: nowrap;
      }
      .run {
        flex: 1;
      }
      .btn[aria-pressed='true'] {
        background: var(--ka-gold);
        color: var(--ka-ink-on-gold);
      }
      .more {
        position: relative;
      }
      .more summary {
        list-style: none;
        box-sizing: border-box;
      }
      .more summary::-webkit-details-marker {
        display: none;
      }
      .more-body {
        position: absolute;
        right: 0;
        bottom: calc(100% + 0.5rem);
        z-index: 5;
        box-sizing: border-box;
        width: min(22rem, calc(100vw - 2rem));
        display: grid;
        gap: 0.75rem;
        box-shadow: var(--ka-lift);
      }
      .more-body p {
        margin: 0;
      }
      .more-body details summary {
        min-height: 44px;
        display: flex;
        align-items: center;
        cursor: pointer;
      }
      .levels {
        list-style: none;
        padding: 0;
        margin: 0;
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(min(100%, 9rem), 1fr));
        gap: 0.5rem;
      }
      .levels a {
        display: grid;
        gap: 0.25rem;
        align-content: start;
        padding: 0.75rem;
        min-height: 44px;
        height: 100%;
        box-sizing: border-box;
        background: var(--ka-stone-800);
        border: 2px solid var(--ka-edge);
        color: var(--ka-parchment);
        text-decoration: none;
      }
      /* Number and title on one line, the status under them: a short card. */
      .levels a {
        grid-template-columns: auto 1fr;
        column-gap: 0.5rem;
        align-items: baseline;
      }
      .levels a > :last-child {
        grid-column: 1 / -1;
      }
      .levels a.next {
        outline: 3px solid var(--ka-focus);
      }
      .group {
        font-size: 1.1rem;
        margin: 0.5rem 0 0;
      }
      .num {
        font: 600 1.5rem var(--ka-font-display);
      }
      .medal {
        font-family: var(--ka-font-display);
        color: var(--ka-gold);
      }
      .small {
        font-size: 0.9rem;
      }
      /* Phones: the bar (or the result sheet) is pinned to the bottom. */
      @media (max-width: 899px) {
        .side {
          display: contents;
        }
        .dock,
        .sheet {
          position: fixed;
          left: 0;
          right: 0;
          bottom: 0;
          z-index: 4;
          padding: 0.75rem 1rem 1rem;
          background: var(--ka-stone-800);
          border-top: 3px solid var(--ka-edge);
          box-shadow: var(--ka-bevel);
        }
        .sheet {
          z-index: 5;
        }
        .sheet + .dock {
          display: none;
        }
      }
      @media (min-width: 900px) {
        .play {
          grid-template-columns: minmax(0, 1fr) 18rem;
          align-items: start;
        }
        .side {
          grid-column: 2;
        }
        .board-col {
          grid-column: 1;
          grid-row: 1;
        }
        .more-body {
          bottom: auto;
          top: calc(100% + 0.5rem);
        }
      }
    `,
  ];

  constructor() {
    super();
    this.levelId = '';
    this.board = { switches: {} };
    this.result = null;
    this.time = 0;
    this.playing = false;
    this.slow = false;
    this.hints = 0;
    this.server = { state: 'idle' };
    this.view = currentFun();
    this.cell = 44;
    this.sheetOpen = false;
    this.walk = false;
    this.walkStep = null;
  }

  private get level() {
    return MINECART_LEVELS.find((l) => l.id === this.levelId);
  }

  override connectedCallback() {
    super.connectedCallback();
    addEventListener('resize', this.measure);
    if (currentKid() && !this.view)
      void loadFun()
        .then((f) => (this.view = f))
        .catch(() => undefined);
  }
  override disconnectedCallback() {
    super.disconnectedCallback();
    this.stop();
    removeEventListener('resize', this.measure);
    this.style.paddingBottom = '';
  }

  override willUpdate(changed: Map<string, unknown>) {
    if (changed.has('levelId')) {
      this.clearResult();
      if (this.level) {
        this.board = loadDraft(this.level);
        this.hints = 0;
      }
      // Leaving level 1 stops the walkthrough; arriving there from How to play starts it.
      this.walk = false;
      if (this.walkNext && this.levelId === WALK_LEVEL) this.beginWalk();
      this.walkNext = false;
    }
  }

  override updated() {
    this.measure();
    // First time on this device: the walkthrough runs from the list or level 1.
    if (!this.walkChecked && currentKid()) {
      this.walkChecked = true;
      if (!walkthroughSeen('minecarts') && (!this.levelId || this.levelId === WALK_LEVEL))
        this.startWalk();
    }
  }

  /** Square size from the yard's box; under 900px, room at the bottom for the pinned bar. */
  private measure = () => {
    const box = this.renderRoot.querySelector<HTMLElement>('.yard-box');
    const dock = this.renderRoot.querySelector<HTMLElement>('.sheet, .dock');
    const pinned = dock && getComputedStyle(dock).position === 'fixed' ? dock.offsetHeight : 0;
    this.style.paddingBottom = pinned ? `${pinned}px` : '';
    const l = this.level;
    if (!box || !l) return;
    // The frame is a 3px border each side. Squares also shrink (not below 44px) so the whole
    // yard fits on the screen above the pinned bar.
    const tall = Math.floor((innerHeight - pinned - 32 - 6) / l.height);
    const size = Math.max(44, Math.min(cellSize(l.width, box.clientWidth - 6), tall));
    if (size !== this.cell) this.cell = size;
  };

  // ---------- playback (simulation time, not wall-clock) ----------

  private get end() {
    return (this.result?.frames.length ?? 1) - 1;
  }
  private get done() {
    return !!this.result && this.time >= this.end;
  }

  private stop() {
    cancelAnimationFrame(this.raf);
    this.playing = false;
  }
  private play() {
    if (!this.result || this.done) return;
    cancelAnimationFrame(this.raf);
    this.playing = true;
    this.last = performance.now();
    const frame = (now: number) => {
      // A long gap (background tab, slow device) advances at most a fifth of a tick, so the
      // playback just resumes where it was.
      const dt = Math.min(now - this.last, TICK_MS / 5);
      this.last = now;
      this.time = Math.min(this.end, this.time + (dt / TICK_MS) * (this.slow ? SLOW : 1));
      if (this.time >= this.end) return this.finish();
      this.raf = requestAnimationFrame(frame);
    };
    this.raf = requestAnimationFrame(frame);
  }
  private step() {
    this.stop();
    this.time = Math.min(this.end, Math.floor(this.time) + 1);
    if (this.done) this.finish();
  }

  /** Plays the run from the start (the end at once under reduced motion). */
  private watch() {
    this.stop();
    this.sheetOpen = false;
    if (matchMedia('(prefers-reduced-motion: reduce)').matches) {
      this.time = this.end;
      this.finish();
    } else {
      this.time = 0;
      this.play();
      // The bar swaps Run for Pause: keep keyboard focus on the bar.
      void this.updateComplete.then(() =>
        this.renderRoot.querySelector<HTMLElement>('.dock .run')?.focus(),
      );
    }
  }

  /** The run has played to its end: open the result sheet and focus its heading. */
  private finish() {
    this.stop();
    this.sheetOpen = true;
    void this.updateComplete.then(() => {
      this.renderRoot.querySelector<HTMLElement>('.sheet h2')?.focus();
      this.renderRoot.querySelector('.cell.marked')?.scrollIntoView({ block: 'center' });
    });
  }

  private async runIt() {
    if (this.walk && this.walkStep !== 'run') return;
    const l = this.level!;
    const r = runCarts(l, this.board);
    if (!r.ok) return;
    if (this.walk) this.coach?.complete('run');
    this.result = r;
    this.server = { state: 'idle' };
    this.watch();
    if (r.outcome !== 'win' || !currentKid()) return;
    this.submit();
  }

  private submit = async () => {
    const l = this.level!;
    this.server = { state: 'saving' };
    try {
      const s = await kidApi<{ medal: Medal | null; newRewards: string[]; fun: FunView }>(
        'POST',
        `/fun/minecarts/${l.id}`,
        { version: l.version, board: this.board },
      );
      setFun(s.fun);
      this.view = s.fun;
      // A late reply for a level the kid has left must not show on the new one.
      if (this.levelId !== l.id) return;
      this.server = { state: 'saved', medal: s.medal, newRewards: s.newRewards };
    } catch (e) {
      if (this.levelId !== l.id) return;
      // API errors carry a status and a kid-friendly message; network errors ("Failed to fetch") don't.
      const api = e instanceof Error && 'status' in e;
      this.server = {
        state: 'error',
        message: api ? e.message : 'Could not save. Check the connection and try again.',
      };
    }
  };

  private clearResult() {
    this.stop();
    this.result = null;
    this.time = 0;
    this.sheetOpen = false;
    this.server = { state: 'idle' };
  }

  private setBoard(b: SwitchBoard) {
    this.board = b;
    this.clearResult();
    if (!this.walk) saveDraft(this.level!, b);
  }
  private toggle(id: string) {
    // In the walkthrough the switch only flips one way (Back and a second tap can't undo it).
    if (this.walk) {
      if (!walkSwitchOk(this.walkStep, id)) return;
      if (!this.board.switches[id])
        this.setBoard({ switches: { ...this.board.switches, [id]: true } });
      return this.coach?.complete('switch');
    }
    const switches = { ...this.board.switches };
    if (switches[id]) delete switches[id];
    else switches[id] = true;
    this.setBoard({ switches });
  }

  private resetSwitches = () => {
    this.closeMenu();
    this.setBoard({ switches: {} });
  };

  /** Closes the sheet (the mark stays) and focuses the marked square, or the first switch. */
  private closeSheet = () => {
    this.sheetOpen = false;
    void this.updateComplete.then(() => {
      const m = this.renderRoot.querySelector<HTMLElement>('.cell.marked');
      (
        m?.querySelector<HTMLElement>('button') ??
        m ??
        this.renderRoot.querySelector<HTMLElement>('.cell button')
      )?.focus();
    });
  };

  private closeMenu() {
    const m = this.renderRoot.querySelector<HTMLDetailsElement>('details.more');
    if (m) m.open = false;
  }

  // ---------- drawing ----------

  private trackSvg(t: Track | undefined) {
    const arm = (d: Dir, stroke = '#b9b2a6', w = 1.6) =>
      svg`<line x1="5" y1="5" x2=${5 + DX[d]! * 5} y2=${5 + DY[d]! * 5} stroke=${stroke} stroke-width=${w} stroke-linecap="square"/>`;
    const sleepers = (d: Dir) =>
      svg`<line x1=${5 + DX[d]! * 3 - DY[d]! * 1.6} y1=${5 + DY[d]! * 3 - DX[d]! * 1.6} x2=${5 + DX[d]! * 3 + DY[d]! * 1.6} y2=${5 + DY[d]! * 3 + DX[d]! * 1.6} stroke="#7a5a3a" stroke-width=".9"/>`;
    let body = svg``;
    if (t?.kind === 'rail')
      body = svg`${t.ends.map((d) => sleepers(d))}${t.ends.map((d) => arm(d))}`;
    else if (t?.kind === 'cross')
      body = svg`${([0, 1, 2, 3] as Dir[]).map((d) => sleepers(d))}${([0, 1, 2, 3] as Dir[]).map((d) => arm(d))}`;
    else if (t?.kind === 'switch') {
      const set = this.board.switches[t.id] ? t.b : t.a;
      const off = set === t.a ? t.b : t.a;
      body = svg`${arm(off, '#5d554a', 1)}${sleepers(t.trunk)}${sleepers(set)}${arm(t.trunk, 'var(--ka-gold)', 1.8)}${arm(set, 'var(--ka-gold)', 1.8)}
        <circle cx="5" cy="5" r="1.4" fill="#000" stroke="var(--ka-gold)" stroke-width=".5"/>`;
    } else if (t?.kind === 'depot') {
      const c = CARGO[t.cargo];
      body = svg`<rect x="1" y="1" width="8" height="8" fill="#1d1a16" stroke=${c.color} stroke-width="1"/>
        <text x="5" y="4.6" font-size="3" text-anchor="middle" fill=${c.color}>${c.shape}</text>
        <text x="5" y="8.2" font-size="3" text-anchor="middle" fill="#fff" font-family="monospace" font-weight="bold">${c.letter}</text>`;
    }
    return svg`<svg viewBox="0 0 10 10" aria-hidden="true">${body}</svg>`;
  }

  /** Carts at the current simulation time, between two frames while they roll. */
  private cartsSvg(l: MinecartLevel) {
    const r = this.result;
    const i = Math.floor(this.time);
    const f = this.time - i;
    const cur = r?.frames[i];
    const next = r?.frames[Math.min(i + 1, this.end)];
    const failed = r && r.outcome !== 'win' && this.done ? r.outcome : null;
    const carts = l.carts.map((spec, k) => {
      const c = CARGO[spec.cargo];
      let x = spec.x;
      let y = spec.y;
      let opacity = r ? 0 : 0.45; // before Run: a faint cart on its loading spot
      if (cur && next) {
        const a: CartFrame = cur[k]!;
        const b = next[k]!;
        if (a.state === 'waiting' && b.state !== 'waiting') {
          x = b.x;
          y = b.y;
          opacity = f;
        } else if (a.state !== 'waiting') {
          const moving = a.state === 'rolling' && b.state !== 'stopped';
          x = moving ? a.x + (b.x - a.x) * f : a.x;
          y = moving ? a.y + (b.y - a.y) * f : a.y;
          opacity = a.state === 'arrived' ? 0.55 : 1;
        }
      }
      const label = svg`<text x="0" y=".08" font-size=".34" text-anchor="middle" fill="#fff">${c.shape}</text>
        <text x="0" y=".38" font-size=".3" text-anchor="middle" fill="#fff" font-family="monospace" font-weight="bold">${c.letter}${k + 1}</text>`;
      return svg`<g transform="translate(${x + 0.5} ${y + 0.5})" opacity=${opacity}>
        <rect x="-.36" y="-.3" width=".72" height=".62" rx=".08" fill=${c.color} stroke="#000" stroke-width=".06"/>
        ${label}
      </g>`;
    });
    const boom =
      failed && ('x' in failed || failed.kind === 'swap')
        ? (() => {
            const at =
              'x' in failed
                ? { x: failed.x, y: failed.y }
                : {
                    x:
                      (r!.frames.at(-1)![failed.carts[0]]!.x +
                        r!.frames.at(-1)![failed.carts[1]]!.x) /
                      2,
                    y:
                      (r!.frames.at(-1)![failed.carts[0]]!.y +
                        r!.frames.at(-1)![failed.carts[1]]!.y) /
                      2,
                  };
            return svg`<g transform="translate(${at.x + 0.78} ${at.y + 0.22}) scale(.5)"><path d="M0 -.5 L.12 -.15 L.48 -.2 L.2 .05 L.35 .42 L0 .2 L-.35 .42 L-.2 .05 L-.48 -.2 L-.12 -.15 Z" fill="var(--ka-gold)" stroke="#000" stroke-width=".04"/></g>`;
          })()
        : nothing;
    return svg`<svg class="carts" viewBox="0 0 ${l.width} ${l.height}" aria-hidden="true">${carts}${boom}</svg>`;
  }

  private switchLabel(t: Extract<Track, { kind: 'switch' }>, x: number, y: number) {
    const thrown = !!this.board.switches[t.id];
    const set = thrown ? t.b : t.a;
    const other = thrown ? t.a : t.b;
    return `Switch ${t.id.slice(1)}, row ${y + 1}, column ${x + 1}. Carts from the ${SIDE[t.trunk]} go ${SIDE[set]}${thrown ? ' (you changed it)' : ''}. Tap to send them ${SIDE[other]}.`;
  }

  // ---------- views ----------

  private levelList() {
    const solved = this.view?.progress.solved.minecarts ?? {};
    const next = nextLevelId(MINECART_LEVELS, solved);
    const done = MINECART_LEVELS.filter((l) => solved[l.id]).length;
    const gold = MINECART_LEVELS.filter((l) => solved[l.id] === 'gold').length;
    return html`<p class="top"><a href="/fun">← Fun</a></p>
      <section class="panel">
        <h1>Minecart Switchyard</h1>
        <p>Flip the switches. Then press Run and get every cart to its depot.</p>
        <p class="small muted">Inspired by Minecraft minecarts, with our own simpler rules.</p>
      </section>
      ${GROUP.map(
        (name, g) =>
          html`<h2 class="group">${name}</h2>
            <ol class="levels">
              ${MINECART_LEVELS.filter((l) => l.carts.length === g + 1).map((l) => {
                const status =
                  l.id === next
                    ? 'Play next →'
                    : solved[l.id] === 'gold'
                      ? '★ Gold'
                      : solved[l.id]
                        ? '✓ Solved'
                        : 'New';
                return html`<li>
                  <a class=${l.id === next ? 'next' : ''} href="/fun/minecarts?level=${l.id}">
                    <span class="num">${MINECART_LEVELS.indexOf(l) + 1}</span>
                    <span>${l.title}</span>
                    <span class=${l.id === next || solved[l.id] ? 'medal' : 'muted'}
                      >${status}</span
                    >
                  </a>
                </li>`;
              })}
            </ol>`,
      )}
      <p class="row">
        <button class="btn" @click=${this.startWalk}>How to play</button>
        <span class="small muted">${done} / ${MINECART_LEVELS.length} · ★ ${gold}</span>
      </p>`;
  }

  private playView(l: MinecartLevel) {
    const idx = MINECART_LEVELS.indexOf(l);
    const r = this.result;
    const running = !!r && !this.done;
    const spot = r && this.done ? failSpot(r) : null;
    return html`<p class="top">
        <a href="/fun/minecarts">← Puzzles</a>
        <span class="title">${idx + 1} · ${l.title}</span>
      </p>
      <div class="play">
        <div class="side">
          <section class="panel goal">
            <p>${l.goal}</p>
            <p class="small muted">
              Gold medal: change ${plural(l.goldChanges, 'switch')} or fewer ·
              ${changed(this.board)} changed
            </p>
            <ul class="carts-list small">
              ${l.carts.map((c, i) => {
                const k = CARGO[c.cargo];
                return html`<li>
                  <span class="tag" style="color:${k.color}">${k.shape} ${k.letter}${i + 1}</span
                  >${cartLabel(l, i)} ·
                  ${c.tick ? `starts after ${plural(c.tick, 'step', 'steps')}` : 'starts at once'}
                </li>`;
              })}
            </ul>
            ${l.hints.slice(0, this.hints).map((h, i) => html`<p class="small">Hint ${i + 1}: ${h}</p>`)}
          </section>
          ${this.sheetOpen && r ? this.sheet(l, r) : nothing}
          <div class="dock">
            <p class="small muted help">
              ${
                running
                  ? `Step ${Math.min(Math.floor(this.time) + 1, this.end)} of ${this.end}. Switches stay put while carts roll.`
                  : 'Tap a switch to flip it. Then press Run.'
              }
            </p>
            <div class="bar">${running ? this.playbackBar() : this.mainBar(l)}</div>
          </div>
        </div>
        <div class="board-col">
          <div class="yard-box">${this.yard(l, running, spot)}</div>
          <p class="small muted">
            Tap a switch to flip it · gold rails: where it sends carts · gold ring: you changed it ·
            ${(['redstone', 'lapis', 'emerald'] as Cargo[])
              .filter((c) => l.carts.some((x) => x.cargo === c))
              .map((c) => `${CARGO[c].shape} ${CARGO[c].letter} ${CARGO_NAME[c]}`)
              .join(', ')}:
            a cart and its depot
          </p>
        </div>
      </div>`;
  }

  private mainBar(l: MinecartLevel) {
    return html`<button class="btn primary run" @click=${this.runIt}>Run</button>
      ${
        this.hints < l.hints.length
          ? html`<button class="btn" ?disabled=${this.walk} @click=${() => this.hints++}>
              Hint
            </button>`
          : nothing
      }
      <details class="more" ?hidden=${this.walk}>
        <summary class="btn" aria-label="More">⋯</summary>
        <div class="panel more-body">
          <p class="row">
            <button class="btn" ?disabled=${!changed(this.board)} @click=${this.resetSwitches}>
              Put switches back
            </button>
            <button class="btn" @click=${this.startWalk}>How to play</button>
          </p>
          <details>
            <summary>How it works</summary>
            <ul class="small">
              <li>Set the switches first. They don't move while the carts roll.</li>
              <li>Each cart starts on its spot and rolls one square every step.</li>
              <li>
                A cart coming into a switch from its gold stem goes the way the gold rails point.
              </li>
              <li>A cart coming in from either side always rolls back onto the stem.</li>
              <li>A crossing (+) goes straight over.</li>
              <li>
                Each cart wants the depot with its letter. Where the rails stop, it falls off.
              </li>
              <li>A cart can't start if another cart is rolling onto its spot.</li>
              <li>Two carts on one square crash. So do two carts meeting head-on.</li>
              <li>Every run starts the same way, so you can try again as much as you like.</li>
            </ul>
          </details>
        </div>
      </details>`;
  }

  private playbackBar() {
    return html`<button class="btn run" @click=${() => (this.playing ? this.stop() : this.play())}>
        ${this.playing ? 'Pause' : 'Play'}
      </button>
      <button class="btn" @click=${this.step}>Step ▶</button>
      <button class="btn" aria-pressed=${this.slow} @click=${() => (this.slow = !this.slow)}>
        Slow
      </button>`;
  }

  private yard(l: MinecartLevel, running: boolean, spot: FailSpot | null) {
    const size = this.cell;
    return html`<div
      class="yard"
      role="group"
      aria-label="Rail yard"
      style="grid-template-columns: repeat(${l.width}, auto); --cell: ${size}px"
    >
      ${Array.from({ length: l.height }, (_, y) =>
        Array.from({ length: l.width }, (_, x) => {
          const t = l.track[`${x},${y}`];
          const marked = !!spot && spot.x === x && spot.y === y;
          const end = x >= l.width / 2;
          const callout = marked
            ? html`<span
                class="callout ${y >= l.height / 2 ? 'up' : ''} ${end ? 'end' : ''}"
                style="--room: ${(end ? x + 1 : l.width - x) * size}px"
                aria-hidden="true"
                >${spot.text}</span
              >`
            : nothing;
          if (t?.kind === 'switch')
            return html`<div class="cell ${marked ? 'marked' : ''}">
              <button
                class=${this.board.switches[t.id] ? 'changed' : ''}
                aria-label=${this.switchLabel(t, x, y) + (marked ? ` ${spot.text}.` : '')}
                ?disabled=${running}
                @click=${() => this.toggle(t.id)}
              >
                ${this.trackSvg(t)}
              </button>
              ${callout}
            </div>`;
          return marked
            ? html`<div
                class="cell marked"
                tabindex="-1"
                role="img"
                aria-label="Row ${y + 1}, column ${x + 1}: ${spot.text}"
              >
                ${this.trackSvg(t)} ${callout}
              </div>`
            : html`<div class="cell">${this.trackSvg(t)}</div>`;
        }),
      )}
      ${this.cartsSvg(l)}
    </div>`;
  }

  private sheet(l: MinecartLevel, r: CartRun) {
    const hint =
      this.hints < l.hints.length
        ? html`<button class="btn" @click=${() => this.hints++}>Hint</button>`
        : nothing;
    if (r.outcome !== 'win') {
      const t = failText(l, r.outcome);
      return html`<section class="panel sheet">
        <h2 tabindex="-1">${t.title}</h2>
        <p>${t.say}</p>
        <p class="small muted">
          ${failSpot(r) ? 'Look at the marked square. ' : ''}Flip a switch and run again.
        </p>
        <p class="row">
          <button class="btn green" @click=${this.closeSheet}>Fix it</button>
          <button class="btn" @click=${this.watch}>Watch again</button>
          ${hint}
        </p>
      </section>`;
    }
    const medal = cartMedal(l, r);
    const next = MINECART_LEVELS[MINECART_LEVELS.indexOf(l) + 1];
    const rewards = (this.server.newRewards ?? []).map(rewardById).filter(Boolean);
    return html`<section class="panel sheet">
      <h2 tabindex="-1">${medal === 'gold' ? '★ Gold medal!' : 'Solved!'}</h2>
      ${medal !== 'gold' ? html`<p>Change ${plural(l.goldChanges, 'switch')} or fewer for gold.</p>` : nothing}
      <p>Every cart got home. You changed ${plural(r.changes, 'switch')}.</p>
      <div aria-live="polite">
        ${this.server.state === 'saving' ? html`<p class="small muted">Saving…</p>` : nothing}
        ${this.server.state === 'error' ? html`<p class="small err">${this.server.message} <button class="btn" @click=${this.submit}>Try again</button></p>` : nothing}
      </div>
      ${
        rewards.length
          ? html`<h3>New for your character</h3>
              <p class="row">
                ${rewards.map((x) => html`<span class="row">${itemIcon(x!)} <strong>${x!.name}</strong></span>`)}
              </p>
              <p><a class="btn" href="/fun/character">Equip it</a></p>`
          : nothing
      }
      <p class="row" ?hidden=${this.walk}>
        ${
          next
            ? html`<button
                class="btn green"
                @click=${() => navigate(`/fun/minecarts?level=${next.id}`)}
              >
                Next puzzle
              </button>`
            : html`<a class="btn green" href="/fun/minecarts">All puzzles</a>`
        }
        <button class="btn" @click=${this.watch}>Watch again</button>
        <button class="btn" @click=${this.closeSheet}>Play again</button>
      </p>
    </section>`;
  }

  // ---------- walkthrough ----------

  private get coach() {
    return this.renderRoot.querySelector<KaWalkthrough>('ka-walkthrough');
  }

  /** Runs the walkthrough on level 1 on a fresh yard; the kid's own level-1 draft is kept. */
  private startWalk = () => {
    markWalkthroughSeen('minecarts');
    this.closeMenu();
    if (this.levelId === WALK_LEVEL) return this.beginWalk();
    this.walkNext = true;
    navigate(`/fun/minecarts?level=${WALK_LEVEL}`);
  };

  private beginWalk() {
    this.clearResult();
    this.board = { switches: {} };
    this.hints = 0;
    this.walkStep = null;
    this.walk = true;
  }

  private onWalkEnd() {
    this.walk = false;
    this.walkStep = null;
    const l = this.level;
    if (!l) return;
    const kept = loadDraft(l);
    // The kid's own switches come back; with none, the walkthrough's yard becomes the draft.
    if (changed(kept)) {
      this.board = kept;
      this.clearResult();
    } else saveDraft(l, this.board);
  }

  override render() {
    if (!currentKid()) return invitation();
    const l = this.level;
    return html`${l ? this.playView(l) : this.levelList()}
      <ka-walkthrough
        .steps=${this.steps}
        ?open=${this.walk}
        @ka-walkthrough-step=${(e: CustomEvent<{ id: string }>) => (this.walkStep = e.detail.id)}
        @ka-walkthrough-end=${this.onWalkEnd}
      ></ka-walkthrough>`;
  }
}
customElements.define('ka-minecarts', KaMinecarts);
